<template>
  <div class="page-vendors bg-[#FFFCF9]">
    <section class="border-b border-slate-200 bg-white">
      <div class="mx-auto max-w-[1900px] px-4 py-5 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between gap-3">
          <div class="min-w-0"><h1 class="text-xl font-bold text-ink sm:text-2xl">Vendors</h1><p class="mt-1 flex min-w-0 items-center gap-1.5 text-sm text-muted"><i class="ri-map-pin-2-fill shrink-0 text-brand"></i><span class="truncate">{{ hasSelectedLocation ? (searchStore.locationText || 'Selected location') : 'Choose a location to start' }}</span></p></div>
          <button type="button" class="inline-flex shrink-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-bold text-ink transition hover:border-brand hover:text-brand sm:rounded-full sm:px-4" @click="openFilters"><i class="ri-equalizer-3-line"></i><span class="hidden sm:inline">Categories</span><span class="sm:hidden">Filters</span><span v-if="selectedCategory !== 'All'" class="h-2 w-2 rounded-full bg-brand"></span></button>
        </div>
        <div v-if="hasSelectedLocation" class="mt-4 grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_auto]">
          <label class="relative block"><span class="sr-only">Search vendors</span><i class="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-slate-400"></i><input v-model.trim="searchTerm" type="search" placeholder="Search vendor name, category, or service" class="h-12 w-full rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-sm font-medium text-ink outline-none transition placeholder:text-slate-400 focus:border-brand focus:ring-4 focus:ring-orange-100"></label>
          <label class="relative"><span class="sr-only">Sort vendors</span><select v-model="sortBy" class="h-12 min-w-[170px] appearance-none rounded-2xl border border-slate-200 bg-white pl-4 pr-10 text-sm font-bold text-ink outline-none transition focus:border-brand"><option value="distance">Nearest first</option><option value="name">Name A–Z</option><option value="deposit">Lowest deposit</option></select><i class="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-muted"></i></label>
        </div>
        <div v-if="hasSelectedLocation && categoryChips.length > 1" class="mt-3 flex gap-2 overflow-x-auto pb-1"><button v-for="chip in categoryChips.slice(0, 8)" :key="chip" type="button" class="shrink-0 rounded-full border px-4 py-2 text-xs font-bold transition" :class="selectedCategory === chip ? 'border-brand bg-brand text-white' : 'border-slate-200 bg-white text-muted hover:border-orange-200 hover:text-brand'" @click="selectedCategory = chip">{{ chip }}</button></div>
      </div>
    </section>

    <section class="mx-auto w-full max-w-[1900px] px-4 py-7 sm:px-6 lg:px-8 lg:py-9">
      <div class="grid gap-7 xl:grid-cols-[minmax(0,1fr)_minmax(500px,44vw)]">
        <div class="order-last min-w-0 xl:order-none">
          <div class="mb-5 flex items-center justify-between gap-4"><h2 class="text-lg font-bold text-ink sm:text-xl">{{ vendorsResultCountTitle }} <span v-if="vendorsResultLocationTitle" class="font-medium text-muted">near {{ vendorsResultLocationTitle }}</span></h2><span v-if="filteredVendors.length" class="hidden rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-muted ring-1 ring-slate-200 sm:inline-flex">{{ filteredVendors.length }} on map</span></div>

          <div v-if="!hasSelectedLocation" class="rounded-[30px] border border-dashed border-orange-200 bg-white px-6 py-14 text-center shadow-[0_12px_35px_rgba(11,33,73,.04)]"><span class="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-brand"><i class="ri-map-pin-search-line text-3xl"></i></span><h3 class="mt-5 text-xl font-bold text-ink">Start with a location</h3><p class="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">Use the location search in the navbar to find event professionals serving your area.</p></div>

          <div v-else-if="loading" class="grid gap-5 md:grid-cols-2"><article v-for="index in 4" :key="index" class="overflow-hidden rounded-[26px] border border-slate-100 bg-white p-3"><div class="aspect-[4/3] animate-pulse rounded-[20px] bg-slate-100"></div><div class="space-y-3 px-2 py-4"><div class="h-5 w-2/3 animate-pulse rounded bg-slate-100"></div><div class="h-4 w-full animate-pulse rounded bg-slate-100"></div><div class="h-4 w-4/5 animate-pulse rounded bg-slate-100"></div></div></article></div>

          <div v-else-if="error" class="flex items-start gap-4 rounded-[26px] border border-red-100 bg-red-50 p-5 text-red-700"><span class="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white"><i class="ri-error-warning-line text-xl"></i></span><div><p class="font-bold">We couldn’t load vendors</p><p class="mt-1 text-sm">{{ error }}</p></div></div>

          <div v-else-if="filteredVendors.length" class="grid gap-5 md:grid-cols-2">
            <article v-for="vendor in filteredVendors" :key="vendor.key" class="group cursor-pointer overflow-hidden rounded-[28px] border border-slate-100 bg-white p-3 shadow-[0_10px_32px_rgba(11,33,73,.06)] transition duration-300 hover:-translate-y-1 hover:border-orange-100 hover:shadow-[0_18px_45px_rgba(11,33,73,.11)]" role="link" tabindex="0" @click="openVendorDetail(vendor)" @keydown.enter.prevent="openVendorDetail(vendor)" @keydown.space.prevent="openVendorDetail(vendor)">
              <div class="relative aspect-[4/3] overflow-hidden rounded-[22px] bg-gradient-to-br from-orange-50 to-[#FFE5EC]"><img v-if="vendor.image" :src="vendor.image" :alt="vendor.name" class="h-full w-full object-cover transition duration-700 group-hover:scale-105"><div v-else class="flex h-full items-center justify-center"><span class="flex h-20 w-20 items-center justify-center rounded-3xl bg-white/80 text-3xl font-bold text-brand shadow-sm">{{ vendor.name.slice(0, 1) }}</span></div><div class="absolute inset-0 bg-gradient-to-t from-ink/45 via-transparent to-transparent"></div><div class="absolute inset-x-0 top-0 flex items-start justify-between gap-3 p-3"><span class="max-w-[70%] truncate rounded-full bg-white/95 px-3 py-1.5 text-[11px] font-bold text-ink shadow-sm backdrop-blur">{{ vendor.category || 'Service vendor' }}</span><span v-if="vendor.distanceLabel" class="rounded-full bg-ink/80 px-3 py-1.5 text-[11px] font-bold text-white backdrop-blur"><i class="ri-navigation-fill mr-1 text-orange-300"></i>{{ vendor.distanceLabel }}</span></div></div>
              <div class="px-2 pb-2 pt-4"><div class="flex items-start justify-between gap-3"><h3 class="line-clamp-1 text-lg font-bold text-ink transition group-hover:text-brand">{{ vendor.name }}</h3><span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-50 text-brand transition group-hover:bg-brand group-hover:text-white"><i class="ri-arrow-right-up-line"></i></span></div><p class="mt-1.5 line-clamp-2 min-h-[44px] text-sm leading-[1.55] text-muted">{{ vendor.tagline || vendor.summary || 'Explore this vendor profile for services and enquiry options.' }}</p><div v-if="vendor.highlights.length" class="mt-3 flex flex-wrap gap-1.5"><span v-for="highlight in vendor.highlights.slice(0, 3)" :key="highlight" class="rounded-full bg-orange-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-brand">{{ highlight }}</span></div><div class="mt-4 border-t border-slate-100 pt-3 text-xs text-muted"><p class="flex items-start gap-2"><i class="ri-map-pin-2-fill mt-0.5 shrink-0 text-brand"></i><span class="min-w-0">{{ displayAddress(vendor) }} <button v-if="shouldTruncateAddress(vendor.location)" type="button" class="address-toggle" @click.stop="toggleAddress(vendor)">{{ isAddressExpanded(vendor) ? 'Less' : 'More' }}</button></span></p><p class="mt-2 flex items-center gap-2"><i class="ri-phone-line text-brand"></i>{{ vendor.phoneLabel || 'Contact available by enquiry' }}</p></div></div>
            </article>
          </div>

          <div v-else class="rounded-[30px] border border-dashed border-orange-200 bg-white px-6 py-14 text-center"><span class="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-brand"><i class="ri-store-2-line text-3xl"></i></span><h3 class="mt-5 text-xl font-bold text-ink">No matching vendors yet</h3><p class="mx-auto mt-2 max-w-md text-sm leading-6 text-muted">Try a broader search, choose another category, or explore a nearby location.</p><button type="button" class="mt-5 rounded-full border border-orange-200 px-5 py-2.5 text-sm font-bold text-brand transition hover:bg-orange-50" @click="clearVendorFilters">Clear filters</button></div>
        </div>

        <aside ref="mapShell" class="order-first self-start xl:order-none xl:sticky xl:top-4"><div class="relative overflow-hidden rounded-[30px] border border-white bg-white p-2 shadow-[0_18px_50px_rgba(11,33,73,.12)] xl:h-[calc(100vh-2rem)]"><div class="pointer-events-none absolute left-6 top-6 z-[500] rounded-full bg-white/95 px-4 py-2 text-xs font-bold text-ink shadow-lg backdrop-blur"><i class="ri-map-2-line mr-1.5 text-brand"></i>Explore the area</div><div class="vendor-map-frame h-full overflow-hidden rounded-[24px] bg-slate-100"><div id="vendor-map" class="vendor-map-surface w-full" :style="mobileMapParallaxStyle"></div></div></div></aside>
      </div>
    </section>

    <transition name="filter-fade">
      <div v-if="showFilterModal" class="fixed inset-0 z-[120] flex justify-end bg-ink/60 backdrop-blur-sm" @click.self="closeFilters">
        <section class="h-full w-full max-w-md overflow-y-auto bg-[#FFFCF9] p-5 shadow-2xl sm:p-7">
          <div class="flex items-start justify-between gap-4 border-b border-orange-100 pb-5">
            <div>
              <p class="text-xs font-bold uppercase tracking-[0.18em] text-brand">Refine your results</p>
              <h2 class="mt-1 text-2xl font-bold text-ink">Vendor categories</h2>
              <p class="mt-1 text-sm text-muted">Choose the service you need for your event.</p>
            </div>
            <button type="button" class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white text-ink transition hover:border-brand hover:text-brand" aria-label="Close filters" @click="closeFilters">
              <i class="ri-close-line text-xl"></i>
            </button>
          </div>

          <p class="mt-6 text-xs font-bold uppercase tracking-[0.14em] text-muted">Service type</p><div class="mt-3 flex flex-wrap gap-2">
            <button
              v-for="chip in categoryChips"
              :key="chip"
              type="button"
              class="rounded-full border px-4 py-2.5 text-sm font-bold transition"
              :class="selectedCategory === chip ? 'border-brand bg-brand text-white shadow-sm' : 'border-slate-200 bg-white text-muted hover:border-brand hover:text-brand'"
              @click="selectedCategory = chip"
            >
              {{ chip }}
            </button>
          </div>
          <button type="button" class="mt-8 w-full rounded-2xl bg-ink px-5 py-3.5 text-sm font-bold text-white transition hover:bg-brand" @click="closeFilters">Show {{ filteredVendors.length }} vendor{{ filteredVendors.length === 1 ? '' : 's' }}</button>
        </section>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import api from '@/services/user/api'
import { useSearchStore } from '@/stores/search'
import { useSeoMeta } from '@/utils/seo'

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

useSeoMeta({
  title: 'Find Event Vendors Near You',
  description: 'Browse local event vendors for catering, photography, decor, entertainment, planning, beauty, and more on Invi8.',
  path: '/vendors'
})

const FALLBACK_IMAGE = '/assets/placeholders/no-vendors.png'

const searchStore = useSearchStore()
const route = useRoute()
const router = useRouter()

const vendors = ref([])
const loading = ref(true)
const error = ref('')
const viewMode = ref('list')
const searchTerm = ref('')
const selectedCategory = ref('All')
const selectedHighlights = ref([])
const sortBy = ref('distance')
const map = ref(null)
const mapShell = ref(null)
const mapParallaxOffset = ref(0)
const mobileMap = ref(null)
const markers = ref([])
const mobileMarkers = ref([])
const locationRequested = ref(false)
const expandedAddressIds = ref(new Set())
const showFilterModal = ref(false)

const selectedCategoryParam = computed(() => {
  if (searchStore.categoryId) return searchStore.categoryId
  return selectedCategory.value && selectedCategory.value !== 'All' ? selectedCategory.value : ''
})

const normalizedVendors = computed(() => vendors.value.map(normalizeVendor))

const categories = computed(() => {
  const values = normalizedVendors.value.map((vendor) => vendor.category).filter(Boolean)
  return ['All', ...Array.from(new Set(values)).sort((a, b) => a.localeCompare(b))]
})

const categoryChips = computed(() => {
  const chips = categories.value.length ? categories.value : ['All']
  if (selectedCategory.value && !chips.includes(selectedCategory.value)) return [...chips, selectedCategory.value]
  return chips
})

const hasSelectedLocation = computed(() => Boolean(searchStore.locationText || (searchStore.lat != null && searchStore.lng != null)))
const mobileMapParallaxStyle = computed(() => ({
  transform: `translate3d(0, ${mapParallaxOffset.value}px, 0)`
}))

const filteredVendors = computed(() => {
  const term = searchTerm.value.toLowerCase()
  const selected = selectedCategory.value

  const list = normalizedVendors.value.filter((vendor) => {
    const matchesTerm = !term || [
      vendor.name,
      vendor.category,
      vendor.tagline,
      vendor.summary,
      vendor.city,
      vendor.state,
      vendor.location,
      ...vendor.highlights
    ].some((value) => String(value || '').toLowerCase().includes(term))

    const matchesCategory = selected === 'All' || vendor.category === selected
    const matchesHighlights = !selectedHighlights.value.length || selectedHighlights.value.every((highlight) => vendor.highlights.includes(highlight))

    return matchesTerm && matchesCategory && matchesHighlights
  })

  return [...list].sort((a, b) => {
    if (sortBy.value === 'name') return a.name.localeCompare(b.name)
    if (sortBy.value === 'deposit') return normalizedNumber(a.depositPercent, 9999) - normalizedNumber(b.depositPercent, 9999)
    return normalizedNumber(a.distance, 999999) - normalizedNumber(b.distance, 999999)
  })
})

const truncateHeadingLocation = (value, maxLength = 30) => {
  const text = String(value || '').trim()
  if (!text) return ''
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text
}

const vendorsResultCountTitle = computed(() => {
  if (!hasSelectedLocation.value) return 'Explore service vendors near you'
  const count = filteredVendors.value.length
  return count ? `${count} vendor${count === 1 ? '' : 's'}` : 'No vendors found nearby'
})

const vendorsResultLocationTitle = computed(() => {
  if (!hasSelectedLocation.value || !filteredVendors.value.length) return ''
  return truncateHeadingLocation(searchStore.locationText || 'your location')
})

const addressKey = (vendor) => String(vendor?.key || vendor?.id || vendor?.name || vendor?.location || '')
const shouldTruncateAddress = (value) => String(value || '').trim().length > 40
const isAddressExpanded = (vendor) => expandedAddressIds.value.has(addressKey(vendor))
const displayAddress = (vendor) => {
  const address = String(vendor?.location || 'Location unavailable').trim()
  if (!shouldTruncateAddress(address) || isAddressExpanded(vendor)) return address
  return `${address.slice(0, 38).trim()}...`
}
const toggleAddress = (vendor) => {
  const key = addressKey(vendor)
  const next = new Set(expandedAddressIds.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedAddressIds.value = next
}

const openVendorDetail = (vendor) => {
  if (!vendor?.link) return
  router.push(vendor.link)
}

const openFilters = () => {
  showFilterModal.value = true
}

const closeFilters = () => {
  showFilterModal.value = false
}

const clearVendorFilters = () => {
  searchTerm.value = ''
  selectedCategory.value = 'All'
  selectedHighlights.value = []
  sortBy.value = 'distance'
}

const loadVendors = async () => {
  loading.value = true
  error.value = ''

  try {
    const formData = new FormData()
    formData.append('id', '')
    formData.append('latitude', searchStore.lat ?? '')
    formData.append('longitude', searchStore.lng ?? '')
    formData.append('category', selectedCategoryParam.value)

    const { data } = await api.getServiceVendorsInvite(formData)
    vendors.value = Array.isArray(data?.data)
      ? data.data
      : Array.isArray(data?.list)
        ? data.list
        : Array.isArray(data?.vendors)
          ? data.vendors
          : []
  } catch (err) {
    console.error(err)
    error.value = 'Failed to load vendors.'
    vendors.value = []
  } finally {
    loading.value = false
  }
}

const ensureMap = async () => {
  await nextTick()
  if (!map.value && document.getElementById('vendor-map')) {
    map.value = createMap('vendor-map')
  }
  if (!mobileMap.value && document.getElementById('vendor-map-mobile')) {
    mobileMap.value = createMap('vendor-map-mobile')
  }
}

let mapParallaxFrame = 0

const updateMapParallax = () => {
  if (typeof window === 'undefined' || mapParallaxFrame) return

  mapParallaxFrame = window.requestAnimationFrame(() => {
    mapParallaxFrame = 0

    if (window.innerWidth >= 1280 || !mapShell.value) {
      mapParallaxOffset.value = 0
      return
    }

    const rect = mapShell.value.getBoundingClientRect()
    const viewportHeight = window.innerHeight || 1
    const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height)
    const clamped = Math.min(1, Math.max(0, progress))
    mapParallaxOffset.value = Math.round((clamped - 0.5) * 28)
  })
}

const createMap = (id) => {
  const instance = L.map(id).setView([20.5937, 78.9629], 5)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(instance)
  return instance
}

const dropMarkers = async () => {
  await ensureMap()
  renderMarkers(map.value, markers)
  renderMarkers(mobileMap.value, mobileMarkers)
}

const renderMarkers = (targetMap, targetMarkers) => {
  if (!targetMap) return

  targetMarkers.value.forEach((marker) => marker.remove())
  targetMarkers.value = []

  filteredVendors.value.forEach((vendor) => {
    if (!vendor.coords) return
    const marker = L.marker([vendor.coords.lat, vendor.coords.lng]).addTo(targetMap)
    marker.bindPopup(getPopupHtml(vendor))
    targetMarkers.value.push(marker)
  })

  if (targetMarkers.value.length) {
    const group = L.featureGroup(targetMarkers.value)
    targetMap.fitBounds(group.getBounds().pad(0.2))
  } else {
    targetMap.setView([20.5937, 78.9629], 5)
  }
}

const getPopupHtml = (vendor) => `
  <div style="width:260px;">
    <div style="height:120px;border-radius:14px;overflow:hidden;background:#f5efe8;margin-bottom:10px;">
      <img src="${escapeHtml(vendor.image)}" alt="${escapeHtml(vendor.name)}" style="width:100%;height:100%;object-fit:cover;" />
    </div>
    <div style="font-weight:700;font-size:15px;line-height:1.25;color:#1f1208;margin-bottom:4px;">${escapeHtml(vendor.name)}</div>
    <div style="font-size:12px;color:#7b624d;margin-bottom:8px;">${escapeHtml(vendor.category || 'Service vendor')} · ${escapeHtml(vendor.location || 'Location unavailable')}</div>
    <a href="${escapeHtml(vendor.link)}" style="display:inline-block;background:#ff310f;color:white;border-radius:999px;padding:8px 12px;font-size:12px;font-weight:700;text-decoration:none;">Open vendor</a>
  </div>
`

const normalizeVendor = (vendor) => {
  const name = cleanText(vendor.brand_name || vendor.brandName || vendor.business_name || vendor.s_business || vendor.s_name || vendor.name || vendor.title || 'Service vendor')
  const category = cleanText(vendor.cat_name || vendor.category_name || vendor.service_vendor_category || vendor.business_category || vendor.category || 'Service vendor')
  const city = cleanText(vendor.city || vendor.s_city || '')
  const state = cleanText(vendor.state || '')
  const address = cleanText(vendor.address || vendor.s_address || vendor.s_address1 || '')
  const tagline = cleanText(vendor.tagline || '')
  const summary = cleanText(stripHtml(vendor.about || vendor.description || vendor.s_desc || vendor.meta_description || vendor.cancel_terms || 'No description added.'))
  const highlights = splitList(vendor.highlights)
  const distance = normalizedNumber(vendor.distance, null)
  const depositPercent = normalizedNumber(vendor.deposit_percent, null)
  const phoneLabel = formatPhone(vendor.phone_code || vendor.s_phone_code, vendor.phone || vendor.s_phone)
  const id = vendor.service_vendor_id || vendor.id || vendor.s_id || vendor.slug || vendor.username || vendor.vendor_id || vendor.business_id || name
  const coords = extractCoords(vendor)

  return {
    raw: vendor,
    key: String(id),
    id,
    name,
    category,
    city,
    state,
    address,
    tagline,
    summary,
    highlights,
    distance,
    distanceLabel: distance != null ? `${distance.toFixed(distance < 10 ? 1 : 0)} km away` : '',
    depositPercent,
    depositLabel: depositPercent != null ? `${depositPercent}%` : 'N/A',
    phoneLabel,
    phoneHref: phoneLabel ? `tel:${phoneLabel.replace(/[^\d+]/g, '')}` : '',
    email: vendor.email || vendor.s_email || '',
    website: normalizeWebsite(vendor.website || vendor.website_link || ''),
    image: getVendorImage(vendor),
    location: [address, city, state].filter(Boolean).join(', ') || 'Location unavailable',
    published: String(vendor.published ?? vendor.status ?? '') === '1',
    coords,
    link: getVendorLink(vendor, id)
  }
}

const extractCoords = (vendor) => {
  const lat = normalizedNumber(vendor.lat ?? vendor.latitude, null)
  const lng = normalizedNumber(vendor.lng ?? vendor.longitude, null)
  if (lat != null && lng != null) return { lat, lng }

  const directionLink = (vendor.links || []).find((link) => (link.title || '').toLowerCase().includes('direction'))
  const [latStr, lngStr] = String(directionLink?.value || '').split(',')
  const parsedLat = normalizedNumber(latStr, null)
  const parsedLng = normalizedNumber(lngStr, null)
  return parsedLat != null && parsedLng != null ? { lat: parsedLat, lng: parsedLng } : null
}

const getVendorImage = (vendor) => {
  const photo = Array.isArray(vendor.photos)
    ? vendor.photos[0]
    : Array.isArray(vendor.gallery)
      ? vendor.gallery[0]
      : Array.isArray(vendor.s_images)
        ? vendor.s_images[0]
        : null

  return vendor.cover_image || vendor.banner_image || vendor.image || vendor.s_user_img || photo?.src || photo?.url || photo?.image || photo?.simg_img || photo || FALLBACK_IMAGE
}

const getVendorLink = (vendor, fallbackId) => {
  const id = vendor.service_vendor_id || vendor.id || vendor.s_id || vendor.slug || vendor.username || vendor.vendor_id || vendor.business_id || fallbackId
  if (!id) return '/service-vendors/detail'

  const query = new URLSearchParams()
  if (searchStore.lat != null) query.set('latitude', searchStore.lat)
  if (searchStore.lng != null) query.set('longitude', searchStore.lng)
  if (selectedCategoryParam.value) query.set('category', selectedCategoryParam.value)
  if (vendor.business_id || vendor.vendor_id || vendor.vendorId) query.set('vendor_id', vendor.business_id || vendor.vendor_id || vendor.vendorId)

  const qs = query.toString()
  return `/service-vendors/detail/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`
}

const hydrateFromQuery = () => {
  const { lat, lng, location, category } = route.query
  const latNum = normalizedNumber(lat, null)
  const lngNum = normalizedNumber(lng, null)

  if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
    searchStore.setLocation({
      text: location || searchStore.locationText || 'Current location',
      lat: latNum,
      lng: lngNum
    })
  }

  if (category && typeof category === 'string') {
    selectedCategory.value = category
  }
}

const ensureLocation = async () => {
  if (locationRequested.value) return
  if (searchStore.lat != null && searchStore.lng != null) return
  locationRequested.value = true
  if (!navigator.geolocation) return

  await new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        searchStore.setLocation({
          text: 'Current location',
          lat: pos.coords.latitude,
          lng: pos.coords.longitude
        })
        resolve()
      },
      () => resolve(),
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })
}

const splitList = (value) => {
  if (Array.isArray(value)) return value.map(cleanText).filter(Boolean)
  return String(value || '').split(',').map(cleanText).filter(Boolean)
}

const stripHtml = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ')
const cleanText = (value) => String(value || '').replace(/\s+/g, ' ').trim()
const normalizedNumber = (value, fallback) => {
  const num = Number.parseFloat(value)
  return Number.isFinite(num) ? num : fallback
}
const formatPhone = (code, phone) => [cleanText(code), cleanText(phone)].filter(Boolean).join(' ')
const normalizeWebsite = (value) => {
  const website = cleanText(value)
  if (!website) return ''
  return /^https?:\/\//i.test(website) ? website : `https://${website}`
}
const escapeHtml = (value) => String(value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

onMounted(async () => {
  hydrateFromQuery()
  updateMapParallax()
  window.addEventListener('scroll', updateMapParallax, { passive: true })
  window.addEventListener('resize', updateMapParallax)
  await ensureLocation()
  await loadVendors()
  await dropMarkers()
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateMapParallax)
  window.removeEventListener('resize', updateMapParallax)
  if (mapParallaxFrame) window.cancelAnimationFrame(mapParallaxFrame)
  if (map.value) map.value.remove()
  if (mobileMap.value) mobileMap.value.remove()
})

watch(
  () => [searchStore.categoryId, searchStore.lat, searchStore.lng],
  async () => {
    await loadVendors()
    await dropMarkers()
  }
)

watch(
  () => route.query,
  async () => {
    hydrateFromQuery()
    await loadVendors()
    await dropMarkers()
  },
  { deep: true }
)

watch([filteredVendors, viewMode], async () => {
  await nextTick()
  if (map.value) map.value.invalidateSize()
  if (mobileMap.value) mobileMap.value.invalidateSize()
  await dropMarkers()
})
</script>

<style scoped>
.page-vendors {
  min-height: 100vh;
}

.address-toggle {
  margin-left: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  color: #ff7a00;
  cursor: pointer;
}

.address-toggle:hover {
  color: #e56700;
}

.filter-fade-enter-active,
.filter-fade-leave-active {
  transition: opacity 0.18s ease;
}

.filter-fade-enter-from,
.filter-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1279px) {
  .vendor-map-frame {
    height: 360px;
  }

  #vendor-map {
    height: calc(100% + 36px);
    margin-top: -18px;
    will-change: transform;
  }
}

@media (max-width: 767px) {
  .vendor-map-frame {
    height: 320px;
  }

  #vendor-map {
    height: calc(100% + 36px);
  }
}

@media (min-width: 1280px) {
  #vendor-map {
    height: 100%;
    margin-top: 0;
  }
}
</style>
